import React, { useState } from 'react';
import { Requirement, RequirementStatus } from '../../types/kyb';
import UploadModal from './UploadModal';

interface RequirementCardProps {
  requirement: Requirement;
  isNew?: boolean;
  onAction?: (requirementId: number | string) => void;
}

const RequirementCard: React.FC<RequirementCardProps> = ({
  requirement,
  isNew = false,
  onAction
}) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  // Status styling
  const getStatusConfig = (status: RequirementStatus) => {
    switch (status) {
      case 'completed':
        return {
          badge: 'Completed',
          badgeClass: 'bg-green-50 text-green-600',
          color: '#059669', // Green-600
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          )
        };
      case 'in_progress':
        return {
          badge: 'In Progress',
          badgeClass: 'bg-primary-50 text-primary-600',
          color: '#1A73E8', // Primary-500
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
          )
        };
      case 'needs_attention':
        return {
          badge: 'Needs Attention',
          badgeClass: 'bg-red-50 text-red-600',
          color: '#DC2626', // Red-600
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )
        };
      case 'upload':
        return {
          badge: 'Upload Required',
          badgeClass: 'bg-amber-50 text-amber-600',
          color: '#D97706', // Amber-600
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          )
        };
      default:
        return {
          badge: 'Pending',
          badgeClass: 'bg-neutral-100 text-neutral-600',
          color: '#9CA3AF', // Neutral-400
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-neutral-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
            </svg>
          )
        };
    }
  };

  const statusConfig = getStatusConfig(requirement.status);

  return (
    <div 
      className={`requirement-card border rounded-xl p-4 mb-4 bg-white transition-all ${
        isNew 
          ? 'border-l-4 border-l-primary-500 border-t border-r border-b border-primary-100 bg-primary-50 shadow-sm' 
          : 'border border-neutral-200 hover:border-primary-200'
      } hover:shadow-card`}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-sm font-medium text-neutral-800 mb-1">{requirement.title}</div>
          <div className="text-xs text-neutral-500 mb-3 leading-relaxed">{requirement.description}</div>
          <div className="flex flex-wrap items-center gap-3">
            <div className={`inline-flex items-center text-xs px-2.5 py-1 rounded-full font-medium ${statusConfig.badgeClass}`}>
              <span className="w-1.5 h-1.5 rounded-full mr-1.5" style={{ backgroundColor: statusConfig.color }}></span>
              {statusConfig.badge}
            </div>
            {requirement.dueDate && (
              <div className="flex items-center text-xs text-neutral-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-neutral-400 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Due: {new Date(requirement.dueDate).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
        <div className="mr-2">
          <div className="w-8 h-8 rounded-full bg-white border border-neutral-200 flex items-center justify-center">
            {statusConfig.icon}
          </div>
        </div>
      </div>

      {/* Child requirements if any */}
      {requirement.children && requirement.children.length > 0 && (
        <div className="mt-3 ml-1 pl-3 border-l-2 border-primary-100">
          {requirement.children.map((child) => (
            <div key={child.id} className="flex items-center text-xs text-neutral-700 py-1.5">
              <div className={`w-1.5 h-1.5 rounded-full mr-2 bg-${
                child.status === 'completed' ? 'primary-500' : 
                child.status === 'in_progress' ? 'secondary-500' : 'neutral-300'
              }`}></div>
              <span className="font-medium">{child.title}</span>
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-sm ${
                getStatusConfig(child.status).badgeClass
              }`}>{getStatusConfig(child.status).badge}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action button for pending, upload, or needs attention items */}
      {(requirement.status === 'pending' || requirement.status === 'needs_attention' || requirement.status === 'upload') && (
        <div className="mt-4">
          <button
            onClick={() => {
              if (requirement.status === 'upload') {
                setIsUploadModalOpen(true);
              } else if (onAction) {
                onAction(requirement.id);
              }
            }}
            className={`text-xs font-medium px-3 py-1.5 rounded-lg shadow-button transition-all ${
              requirement.status === 'needs_attention'
                ? 'bg-white border border-error-500 text-error-500 hover:bg-error-50'
                : requirement.status === 'upload'
                  ? 'bg-white border border-amber-500 text-amber-500 hover:bg-amber-50 hover:border-amber-600'
                  : 'bg-white border border-primary-500 text-primary-500 hover:bg-primary-50 hover:border-primary-600'
            }`}
          >
            {requirement.status === 'needs_attention' 
              ? 'Resolve Issue' 
              : requirement.status === 'upload' 
                ? 'Upload Documents' 
                : 'Complete Requirement'}
          </button>
        </div>
      )}
      
      {/* Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        requirementId={requirement.id}
        onUploadSuccess={() => onAction && onAction(requirement.id)}
      />
    </div>
  );
};

export default RequirementCard;